import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchMessages, sendMessage } from '../../lib/services/messageService';
import { IMessage } from '../../lib/interfaces';
import { reportError } from '../../lib/sentry';

type ApiError = {
  response?: { data?: unknown };
  message?: string;
};

export const loadMessages = createAsyncThunk(
  'messageBoard/loadMessages',
  async (receiverId: string, thunkAPI) => {
    try {
      const response = await fetchMessages(receiverId);
      return response.data.result;
    } catch (err) {
      reportError(err, { tags: { area: 'message_board' } });
      const error = err as ApiError;
      return thunkAPI.rejectWithValue(error.response?.data ?? 'load_failed');
    }
  }
);

export const pollUpdatedMessages = createAsyncThunk(
  'messageBoard/pollUpdatedMessages',
  async (
    { receiverId, afterDate }: { receiverId: string; afterDate: Date },
    thunkAPI
  ) => {
    try {
      const response = await fetchMessages(receiverId, { afterDate });
      return response.data.result;
    } catch (err) {
      reportError(err, { tags: { area: 'message_board' } });
      const error = err as ApiError;
      return thunkAPI.rejectWithValue(error.response?.data ?? 'update_failed');
    }
  }
);

export const createMessage = createAsyncThunk(
  'messageBoard/createMessage',
  async (message: IMessage, thunkAPI) => {
    try {
      thunkAPI.dispatch(addMessageOptimistic(message));

      const response = await sendMessage(message);
      const updatedMessage = response.data.result;

      thunkAPI.dispatch(
        replaceOptimisticMessage({ tempId: message._id, updatedMessage })
      );

      return updatedMessage;
    } catch (err) {
      reportError(err, { tags: { area: 'message_board' } });
      thunkAPI.dispatch(removeMessage(message._id));
      return thunkAPI.rejectWithValue('send_failed');
    }
  }
);

interface MessageBoardState {
  messages: IMessage[];
  messageContent: string;
  loading: boolean;
  error: string | null;
  lastUpdateTimestamp: Date | null;
}

const initialState: MessageBoardState = {
  messages: [],
  messageContent: '',
  loading: false,
  error: null,
  lastUpdateTimestamp: null,
};

const messageBoardSlice = createSlice({
  name: 'messageBoard',
  initialState,
  reducers: {
    updateMessageContent(state, action: PayloadAction<string>) {
      state.messageContent = action.payload;
    },
    addMessageOptimistic(state, action: PayloadAction<IMessage>) {
      state.messages.push(action.payload);
    },
    removeMessage(state, action: PayloadAction<string>) {
      state.messages = state.messages.filter(
        (message) => message._id !== action.payload
      );
    },
    clearMessageContent(state) {
      state.messageContent = '';
    },
    replaceOptimisticMessage(
      state,
      action: PayloadAction<{ tempId: string; updatedMessage: IMessage }>
    ) {
      const { tempId, updatedMessage } = action.payload;
      const index = state.messages.findIndex((msg) => msg._id === tempId);

      if (index !== -1) {
        state.messages[index] = updatedMessage;
      }
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
        state.lastUpdateTimestamp = new Date();
      })
      .addCase(loadMessages.rejected, (state) => {
        state.loading = false;
        state.error = 'load_failed';
      })
      .addCase(createMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(createMessage.rejected, (state) => {
        state.error = 'send_failed';
      })
      .addCase(pollUpdatedMessages.fulfilled, (state, action) => {
        const newMessages = action.payload as IMessage[];
        if (newMessages.length > 0) {
          const existingMessagesMap = new Map(
            state.messages.map((msg) => [msg._id, msg])
          );

          const updatedMessages = state.messages.map((existingMsg) => {
            const updatedMsg = newMessages.find(
              (newMsg) => newMsg._id === existingMsg._id
            );
            return updatedMsg ? { ...existingMsg, ...updatedMsg } : existingMsg;
          });

          const uniqueNewMessages = newMessages.filter(
            (newMsg) => !existingMessagesMap.has(newMsg._id)
          );

          state.messages = [...updatedMessages, ...uniqueNewMessages];

          const latestMessage = newMessages.reduce((latest, current) =>
            new Date(current.updatedAt) > new Date(latest.updatedAt)
              ? current
              : latest
          );
          state.lastUpdateTimestamp = new Date(latestMessage.updatedAt);
        }
      })
      .addCase(pollUpdatedMessages.rejected, (state) => {
        state.error = 'update_failed';
      });
  },
});

export const {
  updateMessageContent,
  addMessageOptimistic,
  removeMessage,
  clearMessageContent,
  replaceOptimisticMessage,
  clearError,
} = messageBoardSlice.actions;

export default messageBoardSlice.reducer;
